import React from 'react';
import { Check, Circle, Settings, ChevronDown, ChevronRight } from 'lucide-react';
import { getConfigurationSteps, MATURITY_CONFIGURATIONS } from '../../config/cisaMaturityModel';

/**
 * ConfigChecklist - Renders configuration steps with completion tracking
 *
 * Props:
 * - functionCode: string (e.g., 'ID_AUTH')
 * - functionName: string (e.g., 'Authentication')
 * - targetLevel: string ('INITIAL' | 'ADVANCED' | 'OPTIMAL')
 * - completedSteps: number[] (indices of completed steps)
 * - onToggleStep: (stepIndex: number) => void
 * - expanded: boolean (default: true)
 * - onToggleExpand: () => void (optional)
 */
const ConfigChecklist = ({
  functionCode,
  functionName,
  targetLevel,
  completedSteps = [],
  onToggleStep,
  expanded = true,
  onToggleExpand
}) => {
  // Get configuration steps
  const steps = getConfigurationSteps(functionCode, targetLevel);

  // Check if we have vendor-specific configs
  const vendorConfigs = MATURITY_CONFIGURATIONS[functionCode]?.[targetLevel];
  const hasVendorDetails = vendorConfigs && vendorConfigs.length > 0;

  // Calculate completion percentage
  const totalSteps = steps.length;
  const completedCount = completedSteps.length;
  const completionPercent = totalSteps > 0 ? Math.round((completedCount / totalSteps) * 100) : 0;

  // Format level name
  const formatLevel = (level) => level.charAt(0) + level.slice(1).toLowerCase();

  if (totalSteps === 0) {
    return (
      <div className="cisa-config-checklist cisa-config-checklist--empty">
        <div className="cisa-config-checklist-header">
          <Settings size={16} className="cisa-config-checklist-icon" />
          <span className="cisa-config-checklist-title">
            Configuration Steps for {formatLevel(targetLevel)}
          </span>
        </div>
        <p className="cisa-config-checklist-empty-text">
          Configuration steps not yet authored for this function.
        </p>
      </div>
    );
  }

  return (
    <div className="cisa-config-checklist">
      <button
        type="button"
        className="cisa-config-checklist-header"
        onClick={onToggleExpand}
      >
        <div className="cisa-config-checklist-header-left">
          {onToggleExpand && (
            expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />
          )}
          <Settings size={16} className="cisa-config-checklist-icon" />
          <span className="cisa-config-checklist-title">
            {functionName}: {formatLevel(targetLevel)} Configuration
          </span>
        </div>

        <div className="cisa-config-checklist-progress">
          <div className="cisa-config-checklist-progress-bar">
            <div
              className="cisa-config-checklist-progress-fill"
              style={{ width: `${completionPercent}%` }}
            />
          </div>
          <span className="cisa-config-checklist-progress-text">
            {completedCount}/{totalSteps} ({completionPercent}%)
          </span>
        </div>
      </button>

      {expanded && (
        <div className="cisa-config-checklist-items">
          {hasVendorDetails ? (
            // Render vendor-specific configs with details
            vendorConfigs.map((config, idx) => {
              const isCompleted = completedSteps.includes(idx);
              return (
                <div key={idx} className="cisa-config-checklist-item cisa-config-checklist-item--vendor">
                  <button
                    type="button"
                    className={`cisa-config-checklist-checkbox ${isCompleted ? 'cisa-config-checklist-checkbox--checked' : ''}`}
                    onClick={() => onToggleStep?.(idx)}
                  >
                    {isCompleted ? <Check size={12} /> : <Circle size={12} />}
                  </button>
                  <div className="cisa-config-checklist-item-content">
                    <div className="cisa-config-checklist-item-meta">
                      <span className="cisa-config-checklist-vendor">{config.vendor}</span>
                      <span className="cisa-config-checklist-product">{config.product}</span>
                    </div>
                    <p className={`cisa-config-checklist-steps ${isCompleted ? 'cisa-config-checklist-steps--completed' : ''}`}>
                      {config.steps}
                    </p>
                  </div>
                </div>
              );
            })
          ) : (
            // Render simple steps
            steps.map((step, idx) => {
              const isCompleted = completedSteps.includes(idx);
              return (
                <div key={idx} className="cisa-config-checklist-item">
                  <button
                    type="button"
                    className={`cisa-config-checklist-checkbox ${isCompleted ? 'cisa-config-checklist-checkbox--checked' : ''}`}
                    onClick={() => onToggleStep?.(idx)}
                  >
                    {isCompleted ? <Check size={12} /> : <Circle size={12} />}
                  </button>
                  <span className={`cisa-config-checklist-step-text ${isCompleted ? 'cisa-config-checklist-step-text--completed' : ''}`}>
                    {step}
                  </span>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

export default ConfigChecklist;
